// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { css } from "styled-components";
import constants from "@store/constants";
import { stickyHeaderHeightVariable } from "@store/Framework/Common/Utilities/getStickyHeaderHeight";
import Zone from "@insite/client-framework/Components/Zone";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import RequestQuoteButton, { RequestQuoteButtonPresentationProps } from "@store/Library/Components/RequestQuoteButton";
import RequestPackagingAdviceButton, {
    RequestPackagingAdviceButtonPresentationProps,
} from "@store/Library/Components/RequestPackagingAdviceButton";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import useZoneIsActive from "@toolkit/Framework/Common/Hooks/useZoneIsActive";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { typographyTitleStyles } from "@store/Themes/common/mixins/typography";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block/Block";
import { productDetailsPrimaryImageMaxWidth } from "@store/Themes/common/props/productDetails";

const sideZoneName = "Side";

const enum fields {
    anchorId = "anchorId",
    title = "title",
}

export interface ProductDetailsGenericProductDescriptionViewProps extends WidgetProps {
    fields: {
        [fields.anchorId]: string;
        [fields.title]: string;
    };
}

export interface ProductDetailsGenericProductDescriptionViewStyles {
    block?: BlockProps;
    wrapper?: InjectableCss;
    mainWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    sideWrapper?: InjectableCss;
    sideInnerWrapper?: InjectableCss;
    sideNarrowWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    requestQuoteButton?: RequestQuoteButtonPresentationProps;
    requestPackagingAdviceButton?: RequestPackagingAdviceButtonPresentationProps;
}

export const productDetailsGenericProductDescriptionViewStyles: ProductDetailsGenericProductDescriptionViewStyles = {
    block: {
        margin: "narrow",
        wrapperCss: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(30, 60)};

            ${breakpoint("md")} {
                flex-direction: row;
            }
        `,
    },
    mainWrapper: {
        css: css`
            flex-grow: 1;
        `,
    },
    title: {
        forwardAs: "h2",
        css: css`
            ${typographyTitleStyles(pxToRemArray([24]))}
            ${spacingStyles(null, "1em")}
        `,
    },
    sideWrapper: {
        css: css`
            flex-shrink: 0;
        `,
    },
    sideInnerWrapper: {
        css: css`
            top: calc(var(${stickyHeaderHeightVariable}) + ${pxToRem(16)});
            position: sticky;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: ${pxToRem(20)};
        `,
    },
    sideNarrowWrapper: {
        css: css`
            width: 100%;
            max-width: ${productDetailsPrimaryImageMaxWidth};
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(12)};
        `,
    },
};

const styles = productDetailsGenericProductDescriptionViewStyles;

const ProductDetailsGenericProductDescriptionView: FC<ProductDetailsGenericProductDescriptionViewProps> = ({
    fields: { anchorId, title },
    id,
}) => {
    const { product } = useContext(ProductContext);
    const sideZoneIsActive = useZoneIsActive(id, sideZoneName);

    return (
        <Block {...styles.block} id={anchorId || undefined}>
            <StyledWrapper {...styles.mainWrapper}>
                {title && <Typography {...styles.title}>{title}</Typography>}
                <Zone contentId={id} zoneName="Main" />
            </StyledWrapper>
            <StyledWrapper {...styles.sideWrapper}>
                <StyledWrapper {...styles.sideInnerWrapper}>
                    {sideZoneIsActive && <Zone contentId={id} zoneName={sideZoneName} />}
                    <StyledWrapper {...styles.sideNarrowWrapper}>
                        <StyledWrapper {...styles.actionsWrapper}>
                            <RequestQuoteButton {...styles.requestQuoteButton} productId={product.id} />
                            <RequestPackagingAdviceButton {...styles.requestPackagingAdviceButton} />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: ProductDetailsGenericProductDescriptionView,
    definition: {
        displayName: "Generic Product Description View",
        group: constants.widgetGroup.productDetails,
        allowedContexts: [constants.pageType.productDetails],
        fieldDefinitions: [
            {
                name: fields.anchorId,
                displayName: "Anchor ID",
                editorTemplate: "TextField",
                defaultValue: "description",
                fieldType: "Contextual",
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Product Details",
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
